import { Head, router } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"
import { QuestionCard } from "@/components/VocationalTest/QuestionCard"
import { ReflectionPresenter } from "@/components/VocationalTest/ReflectionPresenter"
import { StageTransition } from "@/components/VocationalTest/StageTransition"
import { FinalReflection } from "@/components/VocationalTest/FinalReflection"
import type { EnneagramType, ProfileData, Question, Stage } from "@/types/vocational"
import { PageLayout } from "@/components/VocationalTest/PageLayout"

const ANSWERS_KEY = "basic_answers"

interface QuestionsPageProps {
  stages: Stage[]
  profiles: ProfileData[]
}

export default function QuestionsPage({ stages, profiles }: QuestionsPageProps) {
  const [stageIndex, setStageIndex] = useState(0)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [showTransition, setShowTransition] = useState(false)
  const [pendingStageIndex, setPendingStageIndex] = useState<number | null>(null)
  const [showFinalReflection, setShowFinalReflection] = useState(false)
  const [isSubmittingResult, setIsSubmittingResult] = useState(false)
  const [answers, setAnswers] = useState<Record<number, EnneagramType>>(() => {
    const raw = window.localStorage.getItem(ANSWERS_KEY)
    return raw ? JSON.parse(raw) : {}
  })

  const basicStages = useMemo(() => stages.filter((stage) => stage.id !== 10), [stages])
  const allQuestions = useMemo(() => basicStages.flatMap((stage) => stage.questions), [basicStages])
  const currentStage = basicStages[stageIndex]
  const currentQuestion: Question | undefined = currentStage?.questions[questionIndex]

  const totalScoring = useMemo(() => {
    return allQuestions.filter((q) => q.options && q.options.length === 9).length
  }, [allQuestions])

  useEffect(() => {
    const scoringIds = allQuestions
      .filter((q) => q.options && q.options.length === 9)
      .map((q) => q.id)
    window.localStorage.setItem("basic_question_ids", JSON.stringify(scoringIds))
  }, [allQuestions])

  const questionNumber = useMemo(() => {
    if (!currentQuestion) return 0
    let count = 0
    for (let s = 0; s <= stageIndex; s += 1) {
      const stage = basicStages[s]
      if (!stage) continue
      const limit = s === stageIndex ? questionIndex : stage.questions.length - 1
      for (let q = 0; q <= limit; q += 1) {
        const item = stage.questions[q]
        if (item?.options && item.options.length === 9) count += 1
      }
    }
    return count
  }, [basicStages, currentQuestion, questionIndex, stageIndex])

  const persistAnswers = (next: Record<number, EnneagramType>) => {
    setAnswers(next)
    window.localStorage.setItem(ANSWERS_KEY, JSON.stringify(next))
  }

  const goNext = () => {
    const stage = basicStages[stageIndex]
    if (!stage) return

    if (questionIndex < stage.questions.length - 1) {
      setQuestionIndex(questionIndex + 1)
      return
    }

    if (stageIndex < basicStages.length - 1) {
      setPendingStageIndex(stageIndex + 1)
      setShowTransition(true)
      return
    }

    setShowFinalReflection(true)
  }

  const handleAnswer = (answer: EnneagramType) => {
    if (!currentQuestion) return

    if (currentQuestion.options && currentQuestion.options.length === 9) {
      const next = { ...answers, [currentQuestion.id]: answer }
      persistAnswers(next)
    }

    if (currentQuestion.isReflection === true) {
      return
    }

    goNext()
  }

  const handleContinueTransition = () => {
    if (pendingStageIndex === null) return
    setShowTransition(false)
    setStageIndex(pendingStageIndex)
    setQuestionIndex(0)
    setPendingStageIndex(null)
  }

  const handleBackFromTransition = () => {
    setShowTransition(false)
    setPendingStageIndex(null)
    const stage = basicStages[stageIndex]
    if (!stage) return
    setQuestionIndex(Math.max(0, stage.questions.length - 1))
  }

  if (!currentQuestion) {
    return (
      <PageLayout>
        <div className="text-center text-muted-foreground">Nenhuma questao encontrada.</div>
      </PageLayout>
    )
  }

  const isReflection = currentQuestion?.isReflection === true
  const showStageHeader = !isReflection && currentStage

  if (showFinalReflection) {
    const submitBasicResult = async () => {
      if (isSubmittingResult) return

      const counts: Record<EnneagramType, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }
      Object.values(answers).forEach((value) => {
        counts[value] += 1
      })

      const sorted = (Object.entries(counts) as [string, number][])
        .map(([type, count]) => [Number(type) as EnneagramType, count] as [EnneagramType, number])
        .sort((a, b) => b[1] - a[1])
      const dominant = sorted[0]?.[0]
      const profileMatch = dominant ? profiles.find((p) => p.tipo === dominant) : null
      const areaVocacional = profileMatch?.vocationalArea ?? null

      setIsSubmittingResult(true)
      try {
        const response = await fetch("/test-results", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            test_result: {
              completo: true,
              tipo_eneagrama: dominant,
              area_vocacional: areaVocacional,
              pontuacoes_eneagrama: counts,
              respostas: answers
            }
          })
        })
        if (!response.ok) {
          window.alert("Não foi possível salvar seu resultado. Tente novamente.")
          return
        }
        router.visit("/results")
      } finally {
        setIsSubmittingResult(false)
      }
    }

    return (
      <>
        <Head title="Reflexão final" />
        <FinalReflection onContinue={submitBasicResult} />
      </>
    )
  }

  if (showTransition && currentStage && pendingStageIndex !== null) {
    const nextStage = basicStages[pendingStageIndex]
    if (!nextStage) {
      return (
        <PageLayout>
          <div className="text-center text-muted-foreground">Nenhuma etapa encontrada.</div>
        </PageLayout>
      )
    }

    return (
      <>
        <Head title="Transição" />
        <StageTransition
          completedStage={currentStage}
          nextStage={nextStage}
          onBack={handleBackFromTransition}
          onContinue={handleContinueTransition}
        />
      </>
    )
  }

  return (
    <>
      <Head title="Teste" />
      <PageLayout>
        <div className="w-full max-w-3xl space-y-6">
          {showStageHeader && (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-title-blue mb-2">
                Etapa {currentStage.id}: {currentStage.title}
              </h2>
              <p className="text-[#ED7723] font-medium">{currentStage.description}</p>
            </div>
          )}
          {isReflection ? (
            <ReflectionPresenter question={currentQuestion} onContinue={goNext} />
          ) : (
            <QuestionCard
              key={currentQuestion.id}
              question={currentQuestion}
              currentAnswer={answers[currentQuestion.id]}
              onAnswer={handleAnswer}
              questionNumber={Math.max(1, questionNumber)}
              totalQuestions={Math.max(1, totalScoring)}
            />
          )}
        </div>
      </PageLayout>
    </>
  )
}
